<template>
	<div class="test-checkpoint-config">
		<!-- MODE -->
		<b-form-group label="Données d'entrée :" label-size="sm" class="mb-2">
			<b-form-radio-group v-model="mode" :options="mode_options" size="sm" stacked @change="onModeChange" />
		</b-form-group>

		<!-- MODE COPIE -->
		<div v-if="mode === 'copy'" class="mt-1">
			<div class="d-flex align-items-center mb-1">
				<small v-if="snapshot_date" class="text-muted mr-2">Copie du {{ snapshot_date }}</small>
				<small v-else class="text-muted mr-2">Aucune copie enregistrée</small>
				<b-button size="sm" variant="outline-secondary" @click="$emit('refresh')">↻ Rafraîchir</b-button>
			</div>
			<small v-if="!snapshot_date" class="text-muted">Cliquez sur Rafraîchir pour capturer les données de l'étape précédente.</small>
			<edit_tabulator
				v-show="snapshot_date && snapshot_data.length > 0"
				ref="snapshot_tabulator"
				:select_mode="true"
				:extern_file="false"
				@update_row="onSnapshotEdit"
				@add_row="onSnapshotEdit"
				@delete_row="onSnapshotEdit"
			/>
			<!-- Fichiers non-CSV du snapshot -->
			<div v-if="snapshot_files.length > 0" class="mt-1">
				<b-button
					size="sm"
					variant="link"
					v-for="file in snapshot_files"
					:key="file.name"
					@click="downloadFile(file)"
				>{{ file.name }}</b-button>
			</div>
			<!-- URLs (fichiers supplémentaires) du snapshot -->
			<div v-if="snapshot_urls.length > 0" class="mt-1">
				<span v-for="url in snapshot_urls" :key="url.name">
					<b-button size="sm" variant="link" @click="viewUrl(url)">{{ url.name }}</b-button>
					<b-button size="sm" variant="link" style="padding:1px" title="Télécharger" @click="downloadUrl(url)"><i class="fas fa-download"></i></b-button>
				</span>
				<viewFile ref="view_file_cp" v-if="view_file_cp" />
			</div>
		</div>

		<!-- MODE DONNÉES FIXES : mêmes sources que "données initiales" -->
		<div v-if="mode === 'fixed'" class="mt-1">
			<b-form-select v-model="fixed_method" :options="method_options" class="mb-2" size="sm" @change="onModeChange" />
			<div v-if="fixed_method === 'fichier'">
				<edit_tabulator ref="cp_tabulator" :select_mode="true" :extern_file="true" :editor="true" @filename_changed="name_file_fixed = $event" />
			</div>
			<div v-if="fixed_method === 'base de données'">
				<b-form-select v-model="cp_table" :options="tables" class="mb-1" size="sm" />
				<database v-show="cp_table" :name="false" :edit="false" ref="cp_tabulator" />
			</div>
			<div v-if="fixed_method === 'base de données multibases'">
				<DatabaseRequestView :minimal="true" ref="cp_tabulator" />
			</div>
			<div v-if="fixed_method === 'ensemble'">
				<ensemble :name="false" ref="cp_tabulator" />
			</div>
			<div v-if="fixed_method === 'fichiers'">
				<files-upload :name="false" :upload="false" ref="cp_tabulator" />
			</div>
			<div v-if="fixed_method === 'documents'">
				<documents :select="true" ref="cp_tabulator" />
			</div>
			<div v-if="fixed_method === 'Google sheets'">
				<InitDataGsheets ref="cp_tabulator" />
			</div>
			<div v-if="fixed_method === 'Airtable'">
				<InitDataAirtable ref="cp_tabulator" />
			</div>
			<div v-if="fixed_method === 'fichiers - url'">
				<files-upload :name="false" :upload="true" ref="cp_tabulator" />
			</div>
		</div>

		<div class="mt-3">
			<b-button size="sm" variant="outline-danger" @click="$emit('remove')">Supprimer le checkpoint</b-button>
		</div>
	</div>
</template>

<script>
import edit_tabulator from "./edit_tabulator.vue";
import database from "../views/database.vue";
import DatabaseRequestView from "../views/DatabaseRequestView.vue";
import ensemble from "../views/Ensemble.vue";
import file_upload from "./file_upload.vue";
import documents from "../views/Document.vue";
import InitDataGsheets from "./Project_v2/init_data_gsheets.vue";
import InitDataAirtable from "./Project_v2/init_data_airtable.vue";
import * as json2csv from "json2csv";
import treatments from "../services/treatments.js";
import viewFile from "./viewFile.vue";

export default {
	name: 'TestCheckpoint',
	components: {
		edit_tabulator,
		database,
		DatabaseRequestView,
		ensemble,
		'files-upload': file_upload,
		documents,
		InitDataGsheets,
		InitDataAirtable,
		viewFile,
	},
	props: {
		checkpoint: { type: Object, default: null },
		tables: { type: Array, default: () => [] },
	},
	data() {
		return {
			mode: 'live',
			mode_options: [
				{ value: 'live', text: "Données de l'étape précédente (en direct)" },
				{ value: 'copy', text: 'Copie (snapshot à un instant T)' },
				{ value: 'fixed', text: 'Données fixes' },
			],
			method_options: [
				{ value: 'fichier', text: 'Fichier CSV' },
				{ value: 'base de données', text: 'Base de données' },
				{ value: 'base de données multibases', text: 'Base de données multibases' },
				{ value: 'ensemble', text: 'Ensemble' },
				{ value: 'fichiers', text: 'Fichiers' },
				{ value: 'documents', text: 'Documents' },
				{ value: 'Google sheets', text: 'Google Sheets' },
				{ value: 'Airtable', text: 'Airtable' },
				{ value: 'fichiers - url', text: 'Fichiers (URL)' },
			],
			// mode copy
			snapshot_data: [],
			snapshot_files: [],
			snapshot_urls: [],
			snapshot_name_file: 'data.csv',
			snapshot_date: null,
			view_file_cp: false,
			// mode fixed
			fixed_method: 'fichier',
			name_file_fixed: 'data.csv',

			cp_table: '',
		};
	},
	watch: {
		cp_table(val) {
			if (val && this.fixed_method === 'base de données') {
				this.$nextTick(() => {
					if (this.$refs.cp_tabulator && this.$refs.cp_tabulator.init) {
						this.$refs.cp_tabulator.init(val);
					}
				});
			}
		},
		fixed_method(val) {
			if (val === 'base de données' && this.cp_table) {
				this.$nextTick(() => {
					if (this.$refs.cp_tabulator && this.$refs.cp_tabulator.init) {
						this.$refs.cp_tabulator.init(this.cp_table);
					}
				});
			}
		},
		checkpoint: {
			immediate: true,
			handler(cp) {
				if (!cp) return;
				this.mode = cp.mode || 'live';
				this.snapshot_data = cp.data || [];
				this.snapshot_name_file = cp.snapshot_name_file || 'data.csv';
				this.snapshot_files = cp.snapshot_files || [];
				this.snapshot_urls = cp.snapshot_urls || [];
				this.snapshot_date = cp.snapshot_date || null;
				this.fixed_method = cp.fixed_method || 'fichier';
				this.name_file_fixed = cp.name_file_fixed || 'data.csv';
				if (this.mode === 'copy' && this.snapshot_data.length > 0) {
					this.$nextTick(() => {
						if (this.$refs.snapshot_tabulator) {
							this._loading_snapshot = true;
							this.$refs.snapshot_tabulator.load(this.snapshot_data);
							this.$nextTick(() => { this._loading_snapshot = false; });
						}
					});
				}
			},
		},
	},
	methods: {
		onModeChange() {
			this.$emit('update', this.getState());
		},
		onSnapshotEdit() {
			if (this._loading_snapshot) return;
			if (this.$refs.snapshot_tabulator)
				this.snapshot_data = this.$refs.snapshot_tabulator.get_data();
			this.$emit('update', this.getState());
		},
		// Appelé depuis Project_v2 pour stocker un snapshot des données de l'étape précédente
		setSnapshot(data, files = [], name_file = 'data.csv', urls = []) {
			this.snapshot_data = data;
			this.snapshot_files = files || [];
			this.snapshot_urls = urls || [];
			this.snapshot_name_file = name_file || 'data.csv';
			this.snapshot_date = new Date().toLocaleString('fr-FR');
			this.mode = 'copy';
			this.$nextTick(() => {
				if (data.length > 0 && this.$refs.snapshot_tabulator) {
					this._loading_snapshot = true;
					this.$refs.snapshot_tabulator.load(data);
					this.$nextTick(() => { this._loading_snapshot = false; });
				}
			});
			this.$emit('update', this.getState());
		},
		get_name_file() {
			if (this.mode === 'copy') return this.snapshot_name_file || 'data.csv';
			if (this.mode === 'fixed' && this.fixed_method === 'fichier') return this.name_file_fixed || 'data.csv';
			return 'data.csv';
		},
		viewUrl(url) {
			const self = this;
			this.view_file_cp = true;
			this.$nextTick(function() {
				if (self.$refs.view_file_cp) self.$refs.view_file_cp.viewFile(url.url, url.name);
				else treatments.getFileAlyx(url.url, true, 'arraybuffer', url.name);
			});
		},
		downloadUrl(url) {
			treatments.getFileAlyx(url.url, true, 'arraybuffer', url.name);
		},
		downloadFile(file) {
			const url = window.URL.createObjectURL(file);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', file.name);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
		getState() {
			return {
				mode: this.mode,
				data: this.mode === 'copy' ? this.snapshot_data : [],
				snapshot_date: this.mode === 'copy' ? this.snapshot_date : null,
				snapshot_name_file: this.snapshot_name_file,
				snapshot_files: this.snapshot_files,
				snapshot_urls: this.snapshot_urls,
				fixed_method: this.fixed_method,
				name_file_fixed: this.name_file_fixed,
			};
		},
		// Retourne les données (lignes) pour injection dans le test
		async get_data() {
			if (this.mode === 'copy') {
				if (this.$refs.snapshot_tabulator) return this.$refs.snapshot_tabulator.get_data() || [];
				return this.snapshot_data || [];
			}
			if (this.mode === 'fixed' && this.$refs.cp_tabulator && this.$refs.cp_tabulator.get_data)
				return this.$refs.cp_tabulator.get_data() || [];
			return null; // live : résolu par Project_v2
		},
		// Retourne les fichiers pour injection dans le test
		async get_data_files() {
			if (this.mode === 'copy') {
				// Fichiers non-CSV + CSV généré depuis le tabulator si des données existent
				const rows = this.$refs.snapshot_tabulator ? this.$refs.snapshot_tabulator.get_data() || [] : this.snapshot_data;
				const nonCsv = this.snapshot_files || [];
				if (rows.length > 0) {
					const csv = json2csv.parse(rows, { fields: Object.keys(rows[0]) });
					return nonCsv.concat([new File([csv], this.snapshot_name_file || 'data.csv', { type: 'text/csv' })]);
				}
				return nonCsv;
			}
			if (this.mode === 'fixed' && this.$refs.cp_tabulator && this.$refs.cp_tabulator.get_data_files)
				return await this.$refs.cp_tabulator.get_data_files() || [];
			return [];
		},
	},
};
</script>

<style scoped>
.test-checkpoint-config {
	padding: 8px 4px;
}
</style>
